import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { groupServices } from "@/utils/groupServices";

const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200";
const lbl = "text-xs font-medium text-slate-500 mb-1 block";

const LEVEL_TONE = {
  required: "bg-rose-50 text-rose-600", prerequisite: "bg-rose-50 text-rose-600",
  conditional: "bg-amber-50 text-amber-600", recommended: "bg-blue-50 text-blue-600",
  incompatible: "bg-slate-200 text-slate-600",
};
const TYPE_ICON = { service: "GitBranch", document: "FileText", information: "ClipboardList" };

const blankDep = (type = "document") => ({
  id: null, dependency_type: type, target_key: "", target_label: "", requirement_level: "required",
  condition_logic: "all", conditions: [], blocking_level: "production", resolution_methods: [],
  internal_message: "", client_message: "", references: [], reference_note: "", active: true, order: 0, source: "service",
});

/* ─────────────────── shared options hook ─────────────────── */
let _depOptsCache = null;
export function useDepOptions() {
  const [opts, setOpts] = useState(_depOptsCache);
  useEffect(() => {
    if (_depOptsCache) return;
    api.get("/service-library/dependency-options").then((r) => { _depOptsCache = r.data; setOpts(r.data); }).catch(() => {});
  }, []);
  return opts;
}
const label = (arr, v) => (arr || []).find((o) => o.value === v)?.label || v;

/* ─────────────────── Dependency editor form ─────────────────── */
export function DependencyForm({ dep, opts, canManage, onSave, onCancel }) {
  const [d, setD] = useState(() => ({ ...blankDep(), ...(dep || {}) }));
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const ro = !canManage;
  if (!opts) return null;

  const toggleMethod = (m) => set("resolution_methods", d.resolution_methods.includes(m) ? d.resolution_methods.filter((x) => x !== m) : [...d.resolution_methods, m]);
  const addCond = () => set("conditions", [...d.conditions, { field: "", operator: "eq", value: "" }]);
  const updCond = (i, patch) => set("conditions", d.conditions.map((c, ix) => (ix === i ? { ...c, ...patch } : c)));
  const rmCond = (i) => set("conditions", d.conditions.filter((_, ix) => ix !== i));
  const addRef = (t) => set("references", [...d.references, { ref_type: t, collection_id: "", label: "", note: "" }]);
  const updRef = (i, patch) => set("references", d.references.map((r, ix) => (ix === i ? { ...r, ...patch } : r)));
  const rmRef = (i) => set("references", d.references.filter((_, ix) => ix !== i));

  const save = () => {
    if (!d.target_label.trim()) { toast.error("A dependency name/target is required"); return; }
    onSave({ ...d });
  };

  return (
    <div className="border border-blue-200 rounded-xl p-4 bg-blue-50/30 space-y-3" data-testid="dep-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><label className={lbl}>Dependency Type</label>
          <select className={inp} value={d.dependency_type} disabled={ro} data-testid="dep-form-type" onChange={(e) => set("dependency_type", e.target.value)}>
            {(opts.dependency_types || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select></div>
        <div><label className={lbl}>Requirement Level</label>
          <select className={inp} value={d.requirement_level} disabled={ro} data-testid="dep-form-level" onChange={(e) => set("requirement_level", e.target.value)}>
            {(opts.requirement_levels || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select></div>
        <div><label className={lbl}>Blocks (Blocking Stage)</label>
          <select className={inp} value={d.blocking_level} disabled={ro} data-testid="dep-form-blocking" onChange={(e) => set("blocking_level", e.target.value)}>
            {(opts.blocking_levels || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {d.dependency_type === "service" ? (
          <div><label className={lbl}>Depends on Service</label>
            <select className={inp} value={d.target_key || ""} disabled={ro} data-testid="dep-form-service"
              onChange={(e) => { const s = (opts.services || []).find((x) => x.key === e.target.value); set("target_key", e.target.value); set("target_label", s?.name || e.target.value); }}>
              <option value="">— Select a service —</option>
              {groupServices(
                (opts.services || []).map(s => ({ ...s, id: s.key, name: s.name })),
                opts.categories || []
              ).map(({ label, items }) => (
                <optgroup key={label} label={label}>
                  {items.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                </optgroup>
              ))}
            </select></div>
        ) : (
          <div><label className={lbl}>{d.dependency_type === "information" ? "Information Item" : "Document"} Name</label>
            <input className={inp} value={d.target_label} disabled={ro} data-testid="dep-form-target"
              placeholder={d.dependency_type === "information" ? "e.g. Days Outside US" : "e.g. Passport"} onChange={(e) => set("target_label", e.target.value)} /></div>
        )}
        <div><label className={lbl}>Order</label><input type="number" className={inp} value={d.order} disabled={ro} onChange={(e) => set("order", Number(e.target.value) || 0)} /></div>
      </div>

      {/* Trigger conditions */}
      <div className="bg-white rounded-lg border border-slate-200 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-600">Trigger Conditions</span>
          <div className="flex items-center gap-2">
            <select className="border border-slate-300 rounded px-2 py-1 text-xs" value={d.condition_logic} disabled={ro} data-testid="dep-form-logic" onChange={(e) => set("condition_logic", e.target.value)}>
              <option value="all">Match ALL</option><option value="any">Match ANY</option>
            </select>
            {canManage && <button onClick={addCond} data-testid="dep-form-add-condition" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"><Icons.Plus className="h-3 w-3" />Condition</button>}
          </div>
        </div>
        {d.conditions.length === 0 ? <p className="text-xs text-slate-400">Always applies (no conditions).</p> : d.conditions.map((c, i) => (
          <div key={i} className="flex items-center gap-1.5 mb-1.5" data-testid={`dep-cond-${i}`}>
            <input className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs" placeholder="field (e.g. filing_status)" value={c.field} disabled={ro} onChange={(e) => updCond(i, { field: e.target.value })} />
            <select className="border border-slate-300 rounded px-1 py-1 text-xs" value={c.operator} disabled={ro} onChange={(e) => updCond(i, { operator: e.target.value })}>
              {(opts.condition_operators || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs" placeholder="value (e.g. mfj)" value={c.value || ""} disabled={ro} onChange={(e) => updCond(i, { value: e.target.value })} />
            {canManage && <button onClick={() => rmCond(i)} className="text-slate-300 hover:text-rose-500"><Icons.X className="h-3.5 w-3.5" /></button>}
          </div>
        ))}
      </div>

      {/* Resolution methods */}
      <div>
        <label className={lbl}>Resolution Methods (how it can be satisfied)</label>
        <div className="flex flex-wrap gap-1.5" data-testid="dep-form-methods">
          {(opts.resolution_methods || []).map((m) => {
            const on = d.resolution_methods.includes(m.value);
            return <button key={m.value} type="button" disabled={ro} onClick={() => toggleMethod(m.value)}
              className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-500 border-slate-300 hover:border-blue-400"}`}>{m.label}</button>;
          })}
        </div>
      </div>

      {/* Messages */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className={lbl}>Internal Message (staff)</label><textarea className={inp} rows={2} value={d.internal_message || ""} disabled={ro} onChange={(e) => set("internal_message", e.target.value)} /></div>
        <div><label className={lbl}>Client-Facing Message</label><textarea className={inp} rows={2} value={d.client_message || ""} disabled={ro} onChange={(e) => set("client_message", e.target.value)} /></div>
      </div>

      {/* Authoritative references */}
      <div className="bg-white rounded-lg border border-slate-200 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-600">Authoritative References <span className="font-normal text-slate-400">— lets Allison explain WHY (Phase 2)</span></span>
          {canManage && <div className="flex items-center gap-2">
            <button onClick={() => addRef("knowledge_collection")} data-testid="dep-form-add-kref" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"><Icons.Library className="h-3 w-3" />Knowledge</button>
            <button onClick={() => addRef("free_text")} data-testid="dep-form-add-textref" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"><Icons.Plus className="h-3 w-3" />Free-text</button>
          </div>}
        </div>
        {d.references.length === 0 ? <p className="text-xs text-slate-400">No references attached.</p> : d.references.map((r, i) => (
          <div key={i} className="flex items-center gap-1.5 mb-1.5" data-testid={`dep-ref-${i}`}>
            {r.ref_type === "knowledge_collection" ? (
              <select className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs" value={r.collection_id || ""} disabled={ro}
                onChange={(e) => { const c = (opts.knowledge_collections || []).find((x) => x.id === e.target.value); updRef(i, { collection_id: e.target.value, label: c?.name || "" }); }}>
                <option value="">— Select AI Knowledge collection —</option>
                {(opts.knowledge_collections || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            ) : (
              <input className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs" placeholder="Reference (e.g. IRC §6038, IRS Form 5471 Instructions)" value={r.label || ""} disabled={ro} onChange={(e) => updRef(i, { label: e.target.value })} />
            )}
            <span className="text-[10px] text-slate-400 w-20 shrink-0">{r.ref_type === "knowledge_collection" ? "Knowledge" : "Reference"}</span>
            {canManage && <button onClick={() => rmRef(i)} className="text-slate-300 hover:text-rose-500"><Icons.X className="h-3.5 w-3.5" /></button>}
          </div>
        ))}
        <div className="mt-2"><label className={lbl}>Free-text guidance / note</label>
          <textarea className={inp} rows={2} value={d.reference_note || ""} disabled={ro} data-testid="dep-form-refnote"
            placeholder="e.g. Verify whether Category 4 or 5 filing requirements apply before assigning." onChange={(e) => set("reference_note", e.target.value)} /></div>
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={d.active} disabled={ro} onChange={(e) => set("active", e.target.checked)} className="accent-blue-600" />Active</label>
        <div className="flex gap-2">
          <button onClick={onCancel} className="text-sm text-slate-500 px-3">Cancel</button>
          {canManage && <button onClick={save} data-testid="dep-form-save" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-1.5 text-sm font-medium">Save Dependency</button>}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── compact row ─────────────────── */
function DepRow({ d, opts, inherited, disabled, onEdit, onRemove, onToggleDisable, canManage }) {
  const Ic = Icons[TYPE_ICON[d.dependency_type]] || Icons.Circle;
  const dimmed = disabled || d.active === false;
  return (
    <div className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 ${dimmed ? "border-slate-100 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`} data-testid={`dep-row-${d.id || d.target_label}`}>
      <span className="h-7 w-7 rounded-md bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><Ic className="h-3.5 w-3.5" /></span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-800 truncate">{d.target_label || "(unnamed)"}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${LEVEL_TONE[d.requirement_level] || "bg-slate-100 text-slate-500"}`}>{label(opts.requirement_levels, d.requirement_level)}</span>
          {inherited && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600" title={`Inherited from ${d.template_name}`}>Inherited</span>}
        </div>
        <div className="text-[11px] text-slate-400 truncate">
          {label(opts.dependency_types, d.dependency_type)} · blocks {label(opts.blocking_levels, d.blocking_level)}
          {(d.conditions || []).length > 0 && ` · ${d.conditions.length} condition${d.conditions.length > 1 ? "s" : ""}`}
          {(d.references || []).length > 0 && ` · ${d.references.length} ref${d.references.length > 1 ? "s" : ""}`}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {inherited ? (
          canManage && <button onClick={onToggleDisable} title={disabled ? "Enable for this service" : "Disable for this service"} className="text-[11px] px-2 py-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-100" data-testid={`dep-toggle-${d.id}`}>{disabled ? "Enable" : "Disable"}</button>
        ) : canManage && <>
          <button onClick={onEdit} data-testid={`dep-edit-${d.id}`} className="p-1.5 hover:bg-slate-100 rounded text-slate-400"><Icons.Pencil className="h-3.5 w-3.5" /></button>
          <button onClick={onRemove} data-testid={`dep-remove-${d.id}`} className="p-1.5 hover:bg-slate-100 rounded text-rose-400"><Icons.Trash2 className="h-3.5 w-3.5" /></button>
        </>}
      </div>
    </div>
  );
}

/* ─────────────────── Service editor section ─────────────────── */
export function ServiceDependenciesSection({ f, set, canManage, sectionNumber = 7 }) {
  const opts = useDepOptions();
  const [templates, setTemplates] = useState([]);
  const [adding, setAdding] = useState(null); // dep being added/edited or "new"
  const [attachOpen, setAttachOpen] = useState(false);
  useEffect(() => { api.get("/service-library/dependency-templates").then((r) => setTemplates(r.data.items || [])).catch(() => {}); }, []);

  const deps = f.dependencies || [];
  const attached = f.attached_dependency_templates || [];
  const overrides = f.dependency_overrides || [];
  const tmap = Object.fromEntries(templates.map((t) => [t.id, t]));
  const isDisabled = (tid, did) => overrides.some((o) => o.template_id === tid && o.dependency_id === did && o.disabled);

  const inherited = attached.flatMap((tid) => (tmap[tid]?.dependencies || []).map((d) => ({ ...d, template_id: tid, template_name: tmap[tid]?.name })));
  const activeCount = inherited.filter((d) => !isDisabled(d.template_id, d.id)).length + deps.filter((d) => d.active !== false).length;

  const saveDep = (dep) => {
    const withId = { ...dep, id: dep.id || `local-${Date.now()}`, source: "service" };
    set("dependencies", dep.id && deps.some((x) => x.id === dep.id) ? deps.map((x) => (x.id === dep.id ? withId : x)) : [...deps, withId]);
    setAdding(null);
  };
  const removeDep = (id) => set("dependencies", deps.filter((x) => x.id !== id));
  const attachTemplate = (tid) => { if (!attached.includes(tid)) set("attached_dependency_templates", [...attached, tid]); setAttachOpen(false); };
  const detachTemplate = (tid) => { set("attached_dependency_templates", attached.filter((x) => x !== tid)); set("dependency_overrides", overrides.filter((o) => o.template_id !== tid)); };
  const toggleInheritedDisable = (tid, did) => {
    const exists = overrides.find((o) => o.template_id === tid && o.dependency_id === did);
    if (exists) set("dependency_overrides", overrides.map((o) => (o.template_id === tid && o.dependency_id === did ? { ...o, disabled: !o.disabled } : o)));
    else set("dependency_overrides", [...overrides, { template_id: tid, dependency_id: did, disabled: true, overrides: {} }]);
  };

  if (!opts) return null;
  const availableTemplates = templates.filter((t) => !attached.includes(t.id));

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="sl-section-dependencies">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="h-6 w-6 rounded-md bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">{sectionNumber}</span>
          <Icons.Network className="h-4 w-4 text-blue-500" />
          <h3 className="font-heading font-semibold text-slate-800 text-sm">Service Dependencies</h3>
        </div>
        <span className="text-[11px] text-slate-500 bg-slate-50 rounded-full px-2 py-0.5">{activeCount} active · {inherited.length} inherited</span>
      </div>
      <p className="text-xs text-slate-400 mb-4">What must exist before this service can move forward. Live evaluation & gating arrive in <span className="font-medium text-slate-500">Phase 2</span>.</p>

      {/* Attached templates */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-600">Dependency Packages</span>
          {canManage && <div className="relative">
            <button onClick={() => setAttachOpen((s) => !s)} data-testid="dep-attach-btn" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"><Icons.Plus className="h-3.5 w-3.5" />Attach Package</button>
            {attachOpen && <div className="absolute right-0 top-full mt-1 w-64 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg z-20 p-1" data-testid="dep-attach-menu">
              {availableTemplates.length === 0 ? <div className="p-3 text-xs text-slate-400">No packages available. Create one in the Dependency Packages manager.</div> :
                availableTemplates.map((t) => <button key={t.id} onClick={() => attachTemplate(t.id)} className="w-full text-left px-2.5 py-2 rounded hover:bg-slate-50 text-sm"><div className="font-medium text-slate-700">{t.name}</div><div className="text-[11px] text-slate-400">{t.dependency_count} dependencies · v{t.version}</div></button>)}
            </div>}
          </div>}
        </div>
        {attached.length === 0 ? <p className="text-xs text-slate-400">No packages attached.</p> : (
          <div className="flex flex-wrap gap-1.5">
            {attached.map((tid) => <span key={tid} className="inline-flex items-center gap-1 text-xs bg-violet-50 text-violet-700 rounded-full pl-2.5 pr-1 py-0.5" data-testid={`dep-pkg-${tid}`}>{tmap[tid]?.name || tid}{canManage && <button onClick={() => detachTemplate(tid)} className="hover:text-rose-500"><Icons.X className="h-3 w-3" /></button>}</span>)}
          </div>
        )}
      </div>

      {/* Inherited dependencies */}
      {inherited.length > 0 && (
        <div className="space-y-1.5 mb-4">
          <span className="text-xs font-semibold text-slate-600">Inherited from packages</span>
          {inherited.map((d) => <DepRow key={`${d.template_id}-${d.id}`} d={d} opts={opts} inherited disabled={isDisabled(d.template_id, d.id)} canManage={canManage} onToggleDisable={() => toggleInheritedDisable(d.template_id, d.id)} />)}
        </div>
      )}

      {/* Service-specific dependencies */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-600">Service-specific dependencies</span>
        {canManage && !adding && <button onClick={() => setAdding("new")} data-testid="dep-add-btn" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"><Icons.Plus className="h-3.5 w-3.5" />Add Dependency</button>}
      </div>
      {adding && <div className="mb-3"><DependencyForm dep={adding === "new" ? null : adding} opts={opts} canManage={canManage} onSave={saveDep} onCancel={() => setAdding(null)} /></div>}
      {deps.length === 0 && !adding ? <p className="text-xs text-slate-400">No service-specific dependencies. Attach a package above or add one.</p> : (
        <div className="space-y-1.5">
          {deps.map((d) => <DepRow key={d.id} d={d} opts={opts} canManage={canManage} onEdit={() => setAdding(d)} onRemove={() => removeDep(d.id)} />)}
        </div>
      )}
    </div>
  );
}

/* ─────────────────── Dependency Packages (template) manager ─────────────────── */
export function DependencyTemplatesManager({ onClose, canManage }) {
  const opts = useDepOptions();
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null); // template object or "new"
  const load = useCallback(() => api.get("/service-library/dependency-templates", { params: { include_inactive: true } }).then((r) => setList(r.data.items || [])).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);

  const duplicate = async (id) => { try { await api.post(`/service-library/dependency-templates/${id}/duplicate`); toast.success("Duplicated"); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { if (!window.confirm("Delete this package? Services already using it keep their attached copy until re-saved.")) return; try { await api.delete(`/service-library/dependency-templates/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose} data-testid="dep-tpl-manager">
      <div className="w-full max-w-3xl bg-slate-50 h-full overflow-y-auto tf-scroll" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2"><Icons.Package className="h-5 w-5 text-blue-600" /><h2 className="font-heading font-semibold text-slate-900">Dependency Packages</h2></div>
          <div className="flex items-center gap-2">
            {canManage && !editing && <button onClick={() => setEditing("new")} data-testid="dep-tpl-new" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3.5 py-2 text-sm font-medium"><Icons.Plus className="h-4 w-4" />New Package</button>}
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded text-slate-500"><Icons.X className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="p-5">
          {editing ? (
            <TemplateEditor tpl={editing === "new" ? null : editing} opts={opts} canManage={canManage} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
          ) : list === null ? <div className="py-12 text-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500 inline" /></div> :
            list.length === 0 ? <p className="text-sm text-slate-400 text-center py-10">No packages yet. Create reusable dependency sets (e.g. "Standard 1040", "FBAR") and attach them to many services.</p> : (
              <div className="space-y-2">
                {list.map((t) => (
                  <div key={t.id} className={`bg-white rounded-xl border p-4 flex items-center justify-between ${t.active_flag ? "border-slate-200" : "border-slate-200 opacity-60"}`} data-testid={`dep-tpl-${t.id}`}>
                    <button onClick={() => setEditing(t)} className="text-left min-w-0 flex-1">
                      <div className="font-heading font-semibold text-slate-800">{t.name} {!t.active_flag && <span className="text-[10px] text-slate-400">(inactive)</span>}</div>
                      {t.description && <div className="text-xs text-slate-500 truncate">{t.description}</div>}
                      <div className="text-[11px] text-slate-400 mt-1">{t.dependency_count} dependencies · v{t.version}</div>
                    </button>
                    {canManage && <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => setEditing(t)} className="p-1.5 hover:bg-slate-100 rounded text-slate-400" data-testid={`dep-tpl-edit-${t.id}`}><Icons.Pencil className="h-4 w-4" /></button>
                      <button onClick={() => duplicate(t.id)} className="p-1.5 hover:bg-slate-100 rounded text-slate-400" data-testid={`dep-tpl-dup-${t.id}`}><Icons.Copy className="h-4 w-4" /></button>
                      <button onClick={() => del(t.id)} className="p-1.5 hover:bg-slate-100 rounded text-rose-400" data-testid={`dep-tpl-del-${t.id}`}><Icons.Trash2 className="h-4 w-4" /></button>
                    </div>}
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({ tpl, opts, canManage, onClose, onSaved }) {
  const [t, setT] = useState(tpl ? { ...tpl } : { name: "", description: "", active_flag: true, dependencies: [] });
  const [adding, setAdding] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (tpl?.id) api.get(`/service-library/dependency-templates/${tpl.id}`).then((r) => setT(r.data)).catch(() => {});
  }, [tpl]);
  const deps = t.dependencies || [];
  const setDeps = (v) => setT((p) => ({ ...p, dependencies: v }));
  const saveDep = (dep) => {
    const withId = { ...dep, id: dep.id || `local-${Date.now()}`, source: "template" };
    setDeps(dep.id && deps.some((x) => x.id === dep.id) ? deps.map((x) => (x.id === dep.id ? withId : x)) : [...deps, withId]);
    setAdding(null);
  };
  const save = async () => {
    if (!t.name.trim()) { toast.error("Package name is required"); return; }
    setSaving(true);
    const payload = { name: t.name, description: t.description, active_flag: t.active_flag, dependencies: deps };
    try {
      if (tpl?.id) await api.put(`/service-library/dependency-templates/${tpl.id}`, payload);
      else await api.post("/service-library/dependency-templates", payload);
      toast.success("Package saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  if (!opts) return null;
  return (
    <div className="space-y-3" data-testid="dep-tpl-editor">
      <button onClick={onClose} className="text-sm text-slate-500 inline-flex items-center gap-1"><Icons.ArrowLeft className="h-4 w-4" />Back to packages</button>
      <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2"><label className={lbl}>Package Name *</label><input className={inp} value={t.name} data-testid="dep-tpl-name" onChange={(e) => setT({ ...t, name: e.target.value })} placeholder="e.g. Standard 1040" /></div>
        <div className="flex items-end"><label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={t.active_flag} onChange={(e) => setT({ ...t, active_flag: e.target.checked })} className="accent-blue-600" />Active</label></div>
        <div className="sm:col-span-3"><label className={lbl}>Description</label><input className={inp} value={t.description || ""} onChange={(e) => setT({ ...t, description: e.target.value })} /></div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700">Dependencies ({deps.length})</span>
        {canManage && !adding && <button onClick={() => setAdding("new")} data-testid="dep-tpl-add-dep" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"><Icons.Plus className="h-3.5 w-3.5" />Add Dependency</button>}
      </div>
      {adding && <DependencyForm dep={adding === "new" ? null : adding} opts={opts} canManage={canManage} onSave={saveDep} onCancel={() => setAdding(null)} />}
      <div className="space-y-1.5">
        {deps.length === 0 && !adding ? <p className="text-xs text-slate-400">No dependencies yet.</p> :
          deps.map((d) => <DepRow key={d.id} d={d} opts={opts} canManage={canManage} onEdit={() => setAdding(d)} onRemove={() => setDeps(deps.filter((x) => x.id !== d.id))} />)}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className="text-sm border border-slate-200 rounded-lg px-4 py-2 text-slate-600 hover:bg-slate-100">Cancel</button>
        {canManage && <button onClick={save} disabled={saving} data-testid="dep-tpl-save" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-5 py-2 text-sm font-medium disabled:opacity-50">{saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Check className="h-4 w-4" />}Save Package</button>}
      </div>
    </div>
  );
}
